import { Component, Input, OnInit } from '@angular/core';
import { FormComponent } from '../form-component/form.components';
import {
  AbstractControl,
  ControlContainer,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { AddressData } from '../../types/form.type';
declare const google: typeof globalThis.google;

@Component({
  selector: 'hospitality-bot-address-component',
  templateUrl: './address-component.component.html',
  styleUrls: ['./address-component.component.scss'],
})
export class AddressComponent extends FormComponent implements OnInit {
  @Input() controlName: string = 'address';
  @Input() label: string = 'Address';
  addressList;
  addressForm: FormGroup;
  isManualAddress = false;
  isSyncingParentValue = false;
  isSyncingAddressForm = false;

  constructor(
    public controlContainer: ControlContainer,
    private readonly fb: FormBuilder
  ) {
    super(controlContainer);
  }

  ngOnInit(): void {
    this.initForm();
    this.initInputControl();
    const validators = this.inputControl?.validator;

    if (validators) {
      this.addressForm.get('addressData').setValidators(validators);
    }
    this.getValueFromParent();
    this.onValueChange();
  }

  initForm() {
    this.addressForm = this.fb.group({
      addressData: [[]],
      apartment: [''],
      addressLine: [''],
      state: [''],
      postalCode: [''],
      city: [''],
      country: [''],
    });
  }

  getValueFromParent() {
    if (this.inputControl.value) {
      this.addressList = [
        {
          label: this.inputControl.value.formattedAddress,
          value: this.inputControl.value.id,
        },
      ];
      this.addressForm.get('addressData').patchValue({
        label: this.inputControl.value.formattedAddress,
        value: this.inputControl.value.id,
      });
      this.patchManualForm(this.inputControl.value);
    }

    this.inputControl.valueChanges.subscribe((res) => {
      if (this.isSyncingParentValue) return;

      if (res !== null && res !== undefined && res !== '') {
        this.addressList = [
          {
            label: res.formattedAddress,
            value: res.placeId ?? res.id,
          },
        ];
        this.isSyncingAddressForm = true;
        this.addressForm.get('addressData').patchValue(
          {
            label: res.formattedAddress,
            value: res.placeId ?? res.id,
          },
          {
            emitEvent: false,
          }
        );
        this.patchManualForm(res);
        this.isSyncingAddressForm = false;
      } else {
        this.isSyncingAddressForm = true;
        this.addressForm
          .get('addressData')
          .patchValue(null, { emitEvent: false }); // clear the value
        this.addressForm.patchValue(this.emptyManualAddress, {
          emitEvent: false,
        });
        this.isSyncingAddressForm = false;
      }
    });
  }

  onValueChange() {
    this.addressForm.get('addressData').valueChanges.subscribe((res) => {
      if (this.isSyncingAddressForm || this.isManualAddress) return;

      if (res !== null && res !== undefined && res !== '') {
        this.onAddressSelect(res.value).subscribe((res) => {
          this.isSyncingParentValue = true;
          this.inputControl.patchValue(res);
          this.inputControl.markAsDirty();
          this.isSyncingParentValue = false;
        });
      } else {
        this.isSyncingParentValue = true;
        this.inputControl.patchValue(null);
        this.inputControl.markAsDirty();
        this.isSyncingParentValue = false;
      }
    });

    this.manualControlNames.forEach((controlName) => {
      this.addressForm.get(controlName).valueChanges.subscribe(() => {
        if (this.isSyncingAddressForm || !this.isManualAddress) return;
        this.patchParentWithManualAddress();
      });
    });
  }

  /**
   * on input change it will search for key in google places api, and return the list of address as per the input
   */
  searchOptions(text: string) {
    if (text && typeof google !== 'undefined' && google.maps && google.maps.places) {
      const service = new google.maps.places.AutocompleteService();

      service.getPlacePredictions(
        {
          input: text,
        },
        (predictions, status) => {
          if (status == google.maps.places.PlacesServiceStatus.OK) {
            this.addressList = predictions.map((item) => {
              return {
                label: item.description,
                value: item.place_id,
              };
            });
          }
        }
      );
    }
  }

  onAddressSelect(placeId: string) {
    return new Observable((observer) => {
      try {
        if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
          observer.error(new Error('Google Maps is not available'));
          return;
        }

        const placesService = new google.maps.places.PlacesService(
          document.createElement('div')
        );

        placesService.getDetails(
          {
            placeId,
            fields: [
              'address_components',
              'geometry',
              'formatted_address',
              'place_id',
            ],
          },
          (result, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              result
            ) {
              const getAddressComponent = (
                type: string,
                short = false
              ): string => {
                const component = result.address_components?.find((comp) =>
                  comp.types.includes(type)
                );
                return component
                  ? short
                    ? component.short_name
                    : component.long_name
                  : '';
              };

              const location = result.geometry?.location;

              const data: AddressData = {
                placeId: result.place_id,
                formattedAddress: result.formatted_address || '',
                buildingName: getAddressComponent('premise'),
                floor: getAddressComponent('floor'),
                sector: getAddressComponent('sublocality_level_1'),
                street:
                  getAddressComponent('route') ||
                  getAddressComponent('street_address'),
                city: getAddressComponent('locality'),
                state: getAddressComponent('administrative_area_level_1'),
                country: getAddressComponent('country'),
                postalCode: getAddressComponent('postal_code'),
                latitude: location?.lat(),
                longitude: location?.lng(),
                countryCode: getAddressComponent('country', true),
              };

              observer.next(data);
              observer.complete();
            }
          }
        );
      } catch (error) {
        console.error('Error fetching address details:', error);
        observer.error('Error fetching address details');
      }
    });
  }

  useManualAddress() {
    this.isManualAddress = true;
    this.patchManualForm(this.inputControl.value);
    this.setAddressModeValidators();
    this.patchParentWithManualAddress();
  }

  useSearchAddress() {
    this.isManualAddress = false;
    this.setAddressModeValidators();
    this.patchAddressData(this.inputControl.value);
  }

  patchParentWithManualAddress() {
    const manualAddress = this.manualAddressData;
    const hasRequiredError =
      this.isAddressRequired && !this.isManualAddressComplete;
    const value = hasRequiredError
      ? null
      : this.hasManualAddressValue
      ? manualAddress
      : null;

    this.isSyncingParentValue = true;
    this.inputControl.patchValue(value);
    this.inputControl.markAsDirty();
    this.isSyncingParentValue = false;
  }

  patchManualForm(address: AddressData) {
    this.addressForm.patchValue(
      {
        apartment: address?.apartment ?? address?.buildingName ?? '',
        addressLine: address?.addressLine ?? address?.formattedAddress ?? '',
        state: address?.state ?? '',
        postalCode: address?.postalCode ?? '',
        city: address?.city ?? '',
        country: address?.country ?? address?.countryCode ?? '',
      },
      { emitEvent: false }
    );
  }

  patchAddressData(address: AddressData) {
    if (address?.formattedAddress) {
      this.addressForm.get('addressData').patchValue(
        {
          label: address.formattedAddress,
          value: address.placeId ?? address.id,
        },
        { emitEvent: false }
      );
    } else {
      this.addressForm.get('addressData').patchValue(null, {
        emitEvent: false,
      });
    }
  }

  setAddressModeValidators() {
    const addressDataControl = this.addressForm.get('addressData');
    const manualValidators = this.isAddressRequired
      ? [Validators.required]
      : [];

    addressDataControl.setValidators(
      this.isManualAddress && this.isAddressRequired
        ? []
        : this.inputControl?.validator
    );

    this.manualRequiredControlNames.forEach((controlName) => {
      this.addressForm.get(controlName).setValidators(manualValidators);
    });

    this.addressForm.updateValueAndValidity({ emitEvent: false });
  }

  get manualAddressData(): AddressData {
    const value = this.addressForm.getRawValue();
    const addressLine = value.addressLine?.trim() ?? '';
    const apartment = value.apartment?.trim() ?? '';
    const country = value.country?.trim() ?? '';

    return {
      formattedAddress: addressLine,
      addressLine,
      apartment,
      buildingName: apartment,
      state: value.state?.trim() ?? '',
      postalCode: value.postalCode?.trim() ?? '',
      city: value.city?.trim() ?? '',
      country,
      countryCode: country,
    };
  }

  get emptyManualAddress() {
    return {
      apartment: '',
      addressLine: '',
      state: '',
      postalCode: '',
      city: '',
      country: '',
    };
  }

  get manualControlNames() {
    return ['apartment', ...this.manualRequiredControlNames];
  }

  get manualRequiredControlNames() {
    return ['addressLine', 'state', 'postalCode', 'city', 'country'];
  }

  get isAddressRequired() {
    const validator = this.inputControl?.validator;
    if (!validator) return false;

    const errors = validator({} as AbstractControl);
    return !!errors?.required;
  }

  get isManualAddressComplete() {
    return this.manualRequiredControlNames.every((controlName) => {
      const value = this.addressForm.get(controlName).value;
      return typeof value === 'string' ? !!value.trim() : !!value;
    });
  }

  get hasManualAddressValue() {
    return this.manualControlNames.some((controlName) => {
      const value = this.addressForm.get(controlName).value;
      return typeof value === 'string' ? !!value.trim() : !!value;
    });
  }

  get getValidator() {
    const control = this.controlContainer.control.get(this.controlName);
    const errors = control?.errors;

    const targetControl = this.isManualAddress
      ? this.addressForm.get('addressLine')
      : this.addressForm.get('addressData');
    targetControl.setErrors(errors);

    this.setAddressModeValidators();

    if (control?.touched) {
      if (this.isManualAddress) {
        this.manualRequiredControlNames.forEach((controlName) => {
          this.addressForm.get(controlName).markAsTouched();
        });
      } else {
        targetControl.markAsTouched();
      }
    }

    return '';
  }
}
